import type { BrowserCapabilities } from './browserCapabilities'

/**
 * Entitlement (commercial) and capability (technical) are DIFFERENT questions.
 * A tenant can pay for Web Push on a browser that cannot deliver it, and a
 * capable browser can belong to a tenant that has the feature switched off.
 * This module resolves both into one honest UI state.
 *
 * None of this is authorization. The backend enforces every entitlement; the
 * frontend only decides what to show and what to explain.
 */

export const WEB_PLATFORM_FEATURE_KEYS = {
  pwa: 'platform.web_pwa',
  push: 'platform.web_push',
  location: 'platform.web_location',
  offline: 'platform.web_offline',
  backgroundSync: 'platform.web_background_sync',
  camera: 'platform.web_camera',
  qr: 'platform.web_qr',
  voice: 'platform.web_voice',
} as const

export type WebPlatformFeature = keyof typeof WEB_PLATFORM_FEATURE_KEYS

export type WebFeatureAvailability =
  | 'available'
  | 'disabled_by_tenant'
  | 'browser_unsupported'
  | 'permission_required'
  | 'permission_denied'
  | 'installation_required'
  | 'setup_required'

export type BrowserPermissionState = 'granted' | 'denied' | 'prompt' | 'unsupported' | 'unknown'

export interface WebFeatureInput {
  /** Backend `billingEntitlements.features[key].enabled`. */
  entitled: boolean
  /** Feature detection result for this capability. */
  capable: boolean
  /** Browser permission state, when the feature needs one. */
  permission?: BrowserPermissionState
  /** Platform requires a Home Screen install before this can work at all. */
  requiresInstall?: boolean
  /** Deployment-side configuration is missing (e.g. no VAPID keypair). */
  configured?: boolean
}

export interface WebFeatureState {
  availability: WebFeatureAvailability
  /** True only when the feature can be used right now with no further action. */
  usable: boolean
  /** True when a user-facing action (grant, install, enable) can unblock it. */
  actionable: boolean
}

/**
 * Resolution order matters. Commercial availability is reported first so a
 * worker is never told to install an app for a feature their organization has
 * turned off; platform impossibility outranks a permission prompt so we never
 * show an "Enable" button that cannot succeed.
 */
export function resolveWebFeatureState(input: WebFeatureInput): WebFeatureState {
  if (!input.entitled) return { availability: 'disabled_by_tenant', usable: false, actionable: false }
  if (!input.capable) return { availability: 'browser_unsupported', usable: false, actionable: false }
  if (input.requiresInstall) return { availability: 'installation_required', usable: false, actionable: true }
  if (input.configured === false) return { availability: 'setup_required', usable: false, actionable: false }
  if (input.permission === 'denied') return { availability: 'permission_denied', usable: false, actionable: false }
  if (input.permission === 'unsupported') return { availability: 'browser_unsupported', usable: false, actionable: false }
  if (input.permission === 'prompt' || input.permission === 'unknown') {
    return { availability: 'permission_required', usable: false, actionable: true }
  }
  return { availability: 'available', usable: true, actionable: false }
}

export type EntitlementLookup = (featureKey: string) => boolean

/** Convenience resolution for the whole browser feature set. */
export function resolveWebPlatformFeatures(input: {
  capabilities: BrowserCapabilities
  isEntitled: EntitlementLookup
  permissions?: Partial<Record<'geolocation' | 'notifications' | 'camera' | 'microphone', BrowserPermissionState>>
  pushConfigured?: boolean
}): Record<WebPlatformFeature, WebFeatureState> {
  const { capabilities, isEntitled, permissions = {} } = input
  return {
    pwa: resolveWebFeatureState({
      entitled: isEntitled(WEB_PLATFORM_FEATURE_KEYS.pwa),
      capable: capabilities.serviceWorker,
    }),
    push: resolveWebFeatureState({
      entitled: isEntitled(WEB_PLATFORM_FEATURE_KEYS.push),
      // On iOS the PushManager only exists once installed, so the install
      // requirement is evaluated ahead of raw capability.
      capable: capabilities.pushRequiresInstall || (capabilities.push && capabilities.notifications),
      requiresInstall: capabilities.pushRequiresInstall,
      configured: input.pushConfigured,
      permission: permissions.notifications,
    }),
    location: resolveWebFeatureState({
      entitled: isEntitled(WEB_PLATFORM_FEATURE_KEYS.location),
      capable: capabilities.geolocation,
      permission: permissions.geolocation,
    }),
    offline: resolveWebFeatureState({
      entitled: isEntitled(WEB_PLATFORM_FEATURE_KEYS.offline),
      capable: capabilities.indexedDb,
    }),
    backgroundSync: resolveWebFeatureState({
      entitled: isEntitled(WEB_PLATFORM_FEATURE_KEYS.backgroundSync),
      capable: capabilities.backgroundSync,
    }),
    camera: resolveWebFeatureState({
      entitled: isEntitled(WEB_PLATFORM_FEATURE_KEYS.camera),
      capable: capabilities.camera,
      permission: permissions.camera,
    }),
    qr: resolveWebFeatureState({
      entitled: isEntitled(WEB_PLATFORM_FEATURE_KEYS.qr),
      capable: capabilities.barcodeDetector,
      permission: permissions.camera,
    }),
    voice: resolveWebFeatureState({
      entitled: isEntitled(WEB_PLATFORM_FEATURE_KEYS.voice),
      capable: capabilities.microphone,
      permission: permissions.microphone,
    }),
  }
}
